// ── AN FSA-578 IS READ ONCE, WHEN IT ARRIVES, AND THE READ IS KEPT ──────────────
//
// Kevin, 2026-10-19: a 578 arrives BEFORE acres are keyed. Until now nothing kept what
// was read off it: the per-field lines (farm, tract, field, crop, acres, PP, plant date,
// use, irr, org) were thrown away and every consumer — the acreage builder, the 578
// audit — re-ran the whole Opus read (`parse578`, api/_acreage-578.js) on demand.
//
// Now the arrival queue reads it ONCE (api/_578-lines.js) and stores the result in
// `fsa578_lines` (sql/104), one row per farmer_578s row, beside whether the lines TIE to
// the 578's own printed "Farming Operation Totals". Consumers read the stored read when
// it is usable and fall back to a fresh parse when it is not — so nothing breaks before
// a backfill, and nothing changes for a 578 that has never been read.
//
// ⚠ AN UNTIED READ IS STORED, FLAGGED, AND NEVER SERVED AS GOOD. The live parse is not
// deterministic on the printed totals ([[project_578_totals_nondeterminism]]), so a read
// whose rows disagree with a total that IS printed may simply be a bad roll — a consumer
// re-parses rather than inheriting it. "unverified" (no printed total to check: a
// paginated 578 drops them by design) is the SAME trust a live parse gets today, so it is
// served; "untied" is not.
//
// CommonJS, no browser APIs and no imports: api/box.js, api/_acreage.js and
// api/_audit578.js require it, App.jsx imports it, and the checker loads it as-is.

// Bump when the stored SHAPE or the read itself changes. A stored read from another
// version is not served — it is re-parsed, which is the safe direction.
const LINES_PARSER_VERSION = "fsa578-lines-1";

const TIE_STATUSES = Object.freeze(["tied", "untied", "unverified"]);
// What farmer_578s.lines_status may hold (sql/104). `pending` is set ONLY at arrival,
// which is what keeps the 277 rows already on file out of the queue: a backfill is a
// deliberate act (it costs one Opus read each), never a side effect of a deploy.
const LINES_STATUS = Object.freeze({ PENDING: "pending", PARSING: "parsing", FAILED: "failed" });
const MAX_LINES_ATTEMPTS = 3;

// verifyAgainst578's answer → one of three words.
//   untied      a printed total exists for some crop and the rows disagree with it
//   tied        every crop the 578 reports has a printed total and every one agrees
//   unverified  no printed total to check against (or only for some crops, all agreeing)
function tieStatusOf(verify) {
  if (!verify || !Array.isArray(verify.perCrop)) return "unverified";
  if (verify.perCrop.some((c) => c && c.ok === false)) return "untied";
  const checked = verify.perCrop.filter((c) => c && !c.unverified);
  if (!checked.length || checked.length < verify.perCrop.length) return "unverified";
  return "tied";
}

// May a consumer use this stored read instead of parsing the PDF again? A caller that
// reads only the prevent-plant summary (the Adjuster panel) does not select the lines,
// and says so — every other test is the same.
function storedReadUsable(row, { requireLines = true } = {}) {
  if (!row || row.parser_version !== LINES_PARSER_VERSION) return false;
  if (requireLines && (!Array.isArray(row.lines) || !row.lines.length)) return false;
  return row.tie_status === "tied" || row.tie_status === "unverified";
}

// The stored read, in the exact shape parse578 returns, so a consumer cannot tell the
// two apart except by `fromStore`.
function parsedFromStored(row) {
  return {
    rows: (row.lines || []).map((r) => ({ ...r })),
    totals578: row.totals578 || {},
    producerShares: Array.isArray(row.producer_shares) ? row.producer_shares : [],
    cropYear: row.crop_year_578 != null ? Number(row.crop_year_578) : null,
    model: row.model || null,
    practiceColumns: row.practice_columns || null,
    fromStore: true,
    tieStatus: row.tie_status,
  };
}

// The intended use, reduced to the one distinction a prevent-plant cell needs (corn
// grain 016 vs silage 026). Anything else is "" — unknown, never guessed.
function useClass(use) {
  const u = String(use || "").toUpperCase().trim();
  if (["GR", "GRAIN"].includes(u)) return "grain";
  if (["SG", "SILAGE", "SLG", "SILO"].includes(u)) return "silage";
  return "";
}

// Prevented-planting acres per (county, state, crop, use) off the read's own rows. The
// caller passes the rows with processing corn ALREADY removed and `normalizeCrop`
// (api/_acreage-fill.js) — this module holds no copy of the crop dictionary.
function ppSummaryFromRows(rows, normalizeCrop) {
  if (typeof normalizeCrop !== "function") throw new Error("ppSummaryFromRows needs normalizeCrop");
  const m = new Map();
  for (const r of rows || []) {
    if (!r || !r.pp) continue;
    const acres = Number(r.acres);
    if (!(acres > 0)) continue;
    const crop = normalizeCrop(r.crop) || String(r.crop || "").toUpperCase().trim();
    const county = String(r.county || "").toUpperCase().trim();
    const state = String(r.state || "").toUpperCase().trim();
    const use = useClass(r.use);
    const k = [county, state, crop, use].join("|");
    const e = m.get(k) || { county, state, crop, use, acres: 0, fields: 0 };
    e.acres = Math.round((e.acres + acres) * 100) / 100;
    e.fields++;
    m.set(k, e);
  }
  return [...m.values()];
}

// The one fsa578_lines row. Every key always present (PostgREST refuses mixed key sets).
function storedRowFromParse({ farmer578Id, storagePath, sha256, parsed, verify, ppSummary, parseMs }) {
  return {
    farmer_578_id: farmer578Id,
    storage_path: storagePath,
    content_sha256: /^[0-9a-f]{64}$/.test(String(sha256 || "")) ? sha256 : null,
    crop_year_578: parsed && parsed.cropYear != null ? Number(parsed.cropYear) : null,
    lines: (parsed && parsed.rows) || [],
    totals578: (parsed && parsed.totals578) || {},
    producer_shares: (parsed && parsed.producerShares) || [],
    pp_summary: ppSummary || [],
    tie_status: tieStatusOf(verify),
    tie: verify || null,
    model: (parsed && parsed.model) || null,
    practice_columns: (parsed && parsed.practiceColumns) || null,
    parser_version: LINES_PARSER_VERSION,
    parse_ms: Number.isFinite(parseMs) ? Math.round(parseMs) : null,
    parsed_at: new Date().toISOString(),
  };
}

// After a failed read: try again on the next pass, until the cap — then stop and say so.
// A function killed mid-parse leaves `parsing`; that is retried the same way, because the
// attempt was already counted when it began.
function statusAfterFailure(attempts) {
  return Number(attempts) >= MAX_LINES_ATTEMPTS ? LINES_STATUS.FAILED : LINES_STATUS.PENDING;
}
// After a read that stored: the row says what the read said. ⚠ EXCEPT an untied read
// with attempts to spare goes back in the queue. The printed totals are read
// non-deterministically — measured 2026-10-19, the same Stokoe 578 (61 KB, 113 lines)
// read three times came back tied, UNTIED, tied — so one untied read is often a bad roll
// of the totals, not a bad 578. It is stored (flagged) and read again next pass; only an
// untied answer at the cap stands as "untied".
function statusAfterRead(tieStatus, attempts) {
  if (tieStatus === "untied" && Number(attempts) < MAX_LINES_ATTEMPTS) return LINES_STATUS.PENDING;
  return tieStatus;
}
function isQueued(row) {
  if (!row) return false;
  if (row.lines_status === LINES_STATUS.PENDING) return true;
  return row.lines_status === LINES_STATUS.PARSING && Number(row.lines_attempts || 0) < MAX_LINES_ATTEMPTS;
}

// Which of a farm's 578s to read together for ONE crop year. Lifted out of
// api/_audit578.js verbatim so the provisional prevent-plant figure and the audit cannot
// choose different documents:
//   - a NATIONWIDE 578 supersedes the per-county ones (merging double-counts every acre);
//   - ONE document per county, newest wins (the same PDF uploaded twice read 2.00x).
// `docs` must arrive id-ordered — the last write per county is the newest.
function choose578Docs(docs) {
  const nationwide = (docs || []).filter((d) => d && d.is_nationwide);
  const pool = nationwide.length ? nationwide : (docs || []).filter(Boolean);
  const byCounty = new Map();
  for (const d of pool) {
    const key = d.is_nationwide ? "NATIONWIDE" : String(d.county || "").toUpperCase().trim();
    byCounty.set(key, d);
  }
  const use = [...byCounty.values()];
  return { use, superseded: pool.length - use.length };
}

module.exports = {
  LINES_PARSER_VERSION,
  TIE_STATUSES,
  LINES_STATUS,
  MAX_LINES_ATTEMPTS,
  tieStatusOf,
  storedReadUsable,
  parsedFromStored,
  useClass,
  ppSummaryFromRows,
  storedRowFromParse,
  statusAfterFailure,
  statusAfterRead,
  isQueued,
  choose578Docs,
};
